import {useEffect, useRef, useState, type ReactNode} from 'react';
import styled, {keyframes} from 'styled-components';
import {VisuallyHidden} from '../a11y/VisuallyHidden';
import {DIE_PIPS} from './DieRoller';
import {CANDY, FELT, FeltDefs, useFeltIds} from './felt';

/**
 * Map-sized "probability toys": small, self-contained versions of the cute
 * widgets that sit on a roadmap and play when clicked. They carry no visible
 * text; each toy's status is part of its button's accessible name. Each
 * renders as a real button, or as a static picture when `isPreview` (for
 * palette thumbnails, which are buttons themselves).
 *
 * Animations replay by remounting an inner, non-focusable wrapper keyed by a
 * click count, so the focused button itself never remounts.
 */

/** Props shared by every toy. */
export interface MapToyProps {
  /** Rendered width in px. */
  size: number;
  /**
   * Renders a static picture instead of a clickable button. Omitted means
   * interactive.
   */
  isPreview?: boolean;
}

const pop = keyframes`
  0% { transform: translateY(0) scale(1, 1); }
  25% { transform: translateY(0) scale(1.18, 0.82); }
  55% { transform: translateY(-12%) scale(0.9, 1.12); }
  80% { transform: translateY(0) scale(1.05, 0.95); }
  100% { transform: translateY(0) scale(1, 1); }
`;

const spin = keyframes`
  0% { transform: translateY(0) scaleX(1); }
  15% { transform: translateY(-18%) scaleX(0.1); }
  30% { transform: translateY(-34%) scaleX(1); }
  45% { transform: translateY(-40%) scaleX(0.1); }
  60% { transform: translateY(-30%) scaleX(1); }
  75% { transform: translateY(-12%) scaleX(0.1); }
  100% { transform: translateY(0) scaleX(1); }
`;

/** Plays a pop or a coin spin once when it mounts; remount it to replay. */
export const Bounce = styled.div<{$animation: 'pop' | 'spin'}>`
  transform-origin: center bottom;
  animation: ${({$animation}) => ($animation === 'pop' ? pop : spin)} 0.55s
    ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const hop = keyframes`
  0% { transform: translateY(40%) scale(0.3); opacity: 0; }
  55% { transform: translateY(-15%) scale(1.15); opacity: 1; }
  80% { transform: translateY(4%) scale(0.95); }
  100% { transform: translateY(0) scale(1); }
`;

/** An SVG group that hops into place when it mounts. */
export const Hop = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${hop} 0.45s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ToyButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px dashed ${FELT.cardinal};
    outline-offset: 4px;
    border-radius: 12px;
  }
`;

const ToyStatic = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

/**
 * The shell every toy renders in: a button (or a static box in preview)
 * holding the picture, and a polite live region that announces the toy's
 * status after each click, since screen readers do not reliably re-read a
 * focused button whose name changed.
 *
 * @param props.isPreview - Render a static box instead of a button.
 * @param props.label - The toy's accessible name.
 * @param props.caption - The toy's current status, appended to its
 *     accessible name and announced after a click; never shown.
 * @param props.onPoke - Called when the toy is clicked.
 * @param props.children - The toy's picture.
 * @return The shell.
 * @throws Never.
 */
export function ToyShell({
  isPreview,
  label,
  caption,
  onPoke,
  children,
}: {
  isPreview: boolean;
  label: string;
  caption: string;
  onPoke: () => void;
  children: ReactNode;
}) {
  const [hasPoked, setHasPoked] = useState(false);
  if (isPreview) {
    return <ToyStatic>{children}</ToyStatic>;
  }
  return (
    <>
      <ToyButton
        type="button"
        aria-label={`${label}. ${caption}`}
        onClick={() => {
          setHasPoked(true);
          onPoke();
        }}
      >
        {children}
      </ToyButton>
      {/* Empty until the first click, so reading the page does not hear
          every toy's status twice. */}
      <VisuallyHidden role="status" aria-live="polite">
        {hasPoked ? caption : ''}
      </VisuallyHidden>
    </>
  );
}

/**
 * A plush die: click to tumble and roll.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyDie({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [face, setFace] = useState(5);
  const [rolls, setRolls] = useState(0);
  const timersRef = useRef<number[]>([]);
  useEffect(
    () => () => {
      timersRef.current.forEach(timer => {
        window.clearTimeout(timer);
      });
    },
    [],
  );

  const roll = () => {
    setRolls(rolls + 1);
    for (let elapsed = 0; elapsed < 420; elapsed += 70) {
      timersRef.current.push(
        window.setTimeout(() => {
          setFace(1 + Math.floor(Math.random() * 6));
        }, elapsed),
      );
    }
  };

  return (
    <ToyShell
      isPreview={isPreview}
      label="Plush die"
      caption={rolls === 0 ? 'roll me' : `rolled ${face}`}
      onPoke={roll}
    >
      <Bounce key={rolls} $animation="pop">
        <svg viewBox="0 0 140 140" width={size} height={size} aria-hidden>
          <defs>
            <FeltDefs ids={ids} />
          </defs>
          <rect
            x={10}
            y={10}
            width={120}
            height={120}
            rx={32}
            fill={FELT.cream}
            stroke={FELT.creamDark}
            strokeWidth={4}
            filter={`url(#${ids.fuzz})`}
          />
          <rect
            x={22}
            y={22}
            width={96}
            height={96}
            rx={24}
            fill="none"
            stroke={FELT.cardinal}
            strokeWidth={2.4}
            strokeDasharray="6 5"
            opacity={0.4}
          />
          {(DIE_PIPS[face] ?? []).map(([cx, cy]) => (
            <circle
              key={`${cx}-${cy}`}
              cx={cx}
              cy={cy}
              r={11}
              fill={FELT.cardinal}
            />
          ))}
        </svg>
      </Bounce>
    </ToyShell>
  );
}

/**
 * A coin: click to flip; keeps a heads/tails tally.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyCoin({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [flips, setFlips] = useState<boolean[]>([]);
  const isHeads = flips[flips.length - 1] ?? true;
  const heads = flips.filter(Boolean).length;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Coin"
      caption={
        flips.length === 0
          ? 'flip me'
          : `H ${heads} · T ${flips.length - heads}`
      }
      onPoke={() => {
        setFlips([...flips, Math.random() < 0.5]);
      }}
    >
      <Bounce key={flips.length} $animation="spin">
        <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
          <defs>
            <FeltDefs ids={ids} />
          </defs>
          <circle
            cx={50}
            cy={50}
            r={42}
            fill={isHeads ? '#E8B53A' : '#C9CED6'}
            stroke={isHeads ? '#B8862A' : '#8E96A3'}
            strokeWidth={6}
            filter={`url(#${ids.fuzz})`}
          />
          <circle
            cx={50}
            cy={50}
            r={30}
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeDasharray="5 4"
            opacity={0.6}
          />
          {isHeads ? (
            <path
              d="M 50 32 L 55 45 L 69 45 L 58 53 L 62 67 L 50 59 L 38 67 L 42 53 L 31 45 L 45 45 Z"
              fill="#7A5510"
              opacity={0.8}
            />
          ) : (
            <path
              d="M 57 33 A 18 18 0 1 0 57 67 A 14 14 0 1 1 57 33 Z"
              fill="#4E5663"
              opacity={0.75}
            />
          )}
        </svg>
      </Bounce>
    </ToyShell>
  );
}

/** Bean machine geometry, in its 100×120 viewBox. */
const BEAN = {
  rows: 4,
  cx: 50,
  dx: 16,
  dy: 14,
  pegTop: 22,
  floor: 112,
  hopSec: 0.13,
  /** Tallest a bin gets before the machine tips its beans out and restarts. */
  maxBin: 7,
} as const;
const BEAN_BIN_TOP = BEAN.pegTop + BEAN.rows * BEAN.dy;

function beanBinX(bin: number): number {
  return BEAN.cx + (bin - BEAN.rows / 2) * BEAN.dx;
}

/** A bean in flight, animated along its bounce path. */
interface FlyingBean {
  id: number;
  bin: number;
  path: string;
  color: string;
}

/** One bean bouncing down; starts its motion when it mounts. */
function Bean({
  path,
  color,
  hops,
}: {
  path: string;
  color: string;
  hops: number;
}) {
  const motionRef = useRef<SVGElement>(null);
  useEffect(() => {
    const motion = motionRef.current;
    if (motion instanceof SVGAnimationElement) {
      motion.beginElement();
    }
  }, []);
  return (
    <circle r={3.6} fill={color} stroke="#fff" strokeWidth={0.8}>
      <animateMotion
        ref={motionRef}
        begin="indefinite"
        dur={`${hops * BEAN.hopSec}s`}
        fill="freeze"
        path={path}
      />
    </circle>
  );
}

/**
 * A mini Galton board: click to drop three beans; the bins grow toward a
 * bell shape and tip out when full.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyBeanMachine({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [bins, setBins] = useState<number[]>(() => [0, 1, 2, 1, 0]);
  const [flying, setFlying] = useState<FlyingBean[]>([]);
  const [total, setTotal] = useState(0);
  const nextIdRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  useEffect(
    () => () => {
      timersRef.current.forEach(timer => {
        window.clearTimeout(timer);
      });
    },
    [],
  );

  const dropOne = () => {
    let bin = 0;
    const points = [`${BEAN.cx},6`];
    for (let row = 0; row < BEAN.rows; row++) {
      points.push(
        `${BEAN.cx + (bin - row / 2) * BEAN.dx},${BEAN.pegTop + row * BEAN.dy - 6}`,
      );
      if (Math.random() < 0.5) {
        bin++;
      }
    }
    points.push(`${beanBinX(bin)},${BEAN.floor - 4}`);
    const bean: FlyingBean = {
      id: nextIdRef.current++,
      bin,
      path: `M ${points.join(' L ')}`,
      color: CANDY[Math.floor(Math.random() * CANDY.length)] ?? FELT.cardinal,
    };
    setFlying(previous => [...previous, bean]);
    timersRef.current.push(
      window.setTimeout(
        () => {
          setFlying(previous => previous.filter(other => other.id !== bean.id));
          setBins(previous => {
            const next = [...previous];
            next[bean.bin] = (next[bean.bin] ?? 0) + 1;
            return next.some(count => count > BEAN.maxBin)
              ? next.map(() => 0)
              : next;
          });
          setTotal(previous => previous + 1);
        },
        (BEAN.rows + 1) * BEAN.hopSec * 1000,
      ),
    );
  };

  const drop = () => {
    for (let i = 0; i < 3; i++) {
      timersRef.current.push(window.setTimeout(dropOne, i * 160));
    }
  };

  const binStep = (BEAN.floor - BEAN_BIN_TOP - 4) / BEAN.maxBin;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Bean machine"
      caption={total === 0 ? 'drop beans' : `${total} beans`}
      onPoke={drop}
    >
      <svg viewBox="0 0 100 120" width={size} height={size * 1.2} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <rect
          x={4}
          y={2}
          width={92}
          height={116}
          rx={16}
          fill={FELT.board}
          stroke={FELT.creamDark}
          strokeWidth={3}
          filter={`url(#${ids.fuzz})`}
        />
        {Array.from({length: BEAN.rows}, (_, row) =>
          Array.from({length: row + 1}, (__, index) => (
            <circle
              key={`${row}-${index}`}
              cx={BEAN.cx + (index - row / 2) * BEAN.dx}
              cy={BEAN.pegTop + row * BEAN.dy}
              r={3}
              fill={FELT.creamDark}
            />
          )),
        )}
        {bins.map((count, bin) => (
          <rect
            // Bins are fixed positions.
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={bin}
            x={beanBinX(bin) - 6}
            y={BEAN.floor - count * binStep}
            width={12}
            height={Math.max(0.01, count * binStep)}
            rx={3}
            fill={CANDY[bin % CANDY.length] ?? FELT.cardinal}
            opacity={0.85}
            style={{transition: 'y 0.25s, height 0.25s'}}
          />
        ))}
        <path
          d={`M 10 ${BEAN.floor + 1} H 90`}
          stroke={FELT.creamDark}
          strokeWidth={3}
          strokeLinecap="round"
        />
        {flying.map(bean => (
          <Bean
            key={bean.id}
            path={bean.path}
            color={bean.color}
            hops={BEAN.rows + 1}
          />
        ))}
      </svg>
    </ToyShell>
  );
}

/** Dart board geometry, in its 100×100 viewBox. */
const DART_BOARD = {x: 8, y: 8, size: 84} as const;
/** Most darts drawn at once; older ones still count. */
const MAX_DARTS = 40;

/**
 * A dart board estimating π: click to throw a dart; four times the share
 * inside the quarter circle estimates π.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyDarts({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [darts, setDarts] = useState<Array<{u: number; v: number}>>([]);
  const [inside, setInside] = useState(0);
  const [total, setTotal] = useState(0);
  const {x, y, size: side} = DART_BOARD;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Pi darts"
      caption={
        total === 0
          ? 'throw a dart'
          : `π ≈ ${((4 * inside) / total).toFixed(2)}`
      }
      onPoke={() => {
        const dart = {u: Math.random(), v: Math.random()};
        setDarts([...darts, dart].slice(-MAX_DARTS));
        setInside(inside + (dart.u * dart.u + dart.v * dart.v <= 1 ? 1 : 0));
        setTotal(total + 1);
      }}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <rect
          x={x}
          y={y}
          width={side}
          height={side}
          rx={8}
          fill={FELT.cream}
          stroke={FELT.creamDark}
          strokeWidth={3}
          filter={`url(#${ids.fuzz})`}
        />
        <path
          d={`M ${x} ${y + side} L ${x} ${y} A ${side} ${side} 0 0 1 ${x + side} ${y + side} Z`}
          fill="#F4C9C4"
        />
        {darts.map(({u, v}, index) => {
          const isLatest = index === darts.length - 1;
          const dot = (
            <circle
              cx={x + u * side}
              cy={y + (1 - v) * side}
              r={isLatest ? 4.2 : 3}
              fill={u * u + v * v <= 1 ? FELT.cardinal : FELT.blue}
              stroke="#fff"
              strokeWidth={0.8}
            />
          );
          return isLatest ? (
            <Hop key={`latest-${total}`}>{dot}</Hop>
          ) : (
            // Drawn darts never move or reorder.
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <g key={index}>{dot}</g>
          );
        })}
      </svg>
    </ToyShell>
  );
}

/** The jar's marbles: color, name, and how many. */
const JAR_MARBLES = [
  {color: FELT.cardinal, name: 'red', count: 5},
  {color: FELT.blue, name: 'blue', count: 3},
  {color: FELT.gold, name: 'gold', count: 2},
] as const;
const JAR_TOTAL = JAR_MARBLES.reduce((sum, marble) => sum + marble.count, 0);

/** Marble spots inside the jar, bottom rows first. */
const JAR_SPOTS = [
  [30, 90],
  [45, 91],
  [60, 90],
  [37, 78],
  [52, 78],
  [66, 77],
  [30, 66],
  [45, 65],
  [60, 66],
  [52, 53],
] as const;

/**
 * A jar of marbles: click to draw one (with replacement); it hops out and
 * the caption names its color and chance.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyMarbleJar({size, isPreview = false}: MapToyProps) {
  const [draws, setDraws] = useState<number[]>([]);
  const latest = draws[draws.length - 1];
  const drawn = latest === undefined ? undefined : JAR_MARBLES[latest];
  const colors = JAR_MARBLES.flatMap(marble =>
    Array.from({length: marble.count}, () => marble.color),
  );

  const draw = () => {
    let pick = Math.random() * JAR_TOTAL;
    const index = JAR_MARBLES.findIndex(marble => {
      pick -= marble.count;
      return pick < 0;
    });
    setDraws([...draws, Math.max(0, index)]);
  };

  return (
    <ToyShell
      isPreview={isPreview}
      label="Marble jar"
      caption={
        drawn === undefined
          ? 'draw a marble'
          : `${drawn.name} · ${Math.round((drawn.count / JAR_TOTAL) * 100)}%`
      }
      onPoke={draw}
    >
      <svg
        viewBox="0 0 90 110"
        width={size}
        height={size * (110 / 90)}
        aria-hidden
      >
        <rect x={26} y={24} width={38} height={10} rx={4} fill="#C9A77C" />
        <rect
          x={14}
          y={32}
          width={62}
          height={72}
          rx={16}
          fill="#DCEFF7"
          opacity={0.85}
          stroke="#A9CFE0"
          strokeWidth={2.5}
        />
        {JAR_SPOTS.map(([cx, cy], index) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r={6.5}
            fill={colors[index] ?? FELT.cardinal}
          />
        ))}
        <path
          d="M 20 44 v 44"
          stroke="#fff"
          strokeWidth={3}
          strokeLinecap="round"
          opacity={0.6}
        />
        {drawn !== undefined && (
          <Hop key={draws.length}>
            <circle cx={45} cy={12} r={9} fill={drawn.color} />
            <circle cx={42} cy={9} r={2.6} fill="#fff" opacity={0.6} />
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}
